import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Sparkles, Loader2 } from 'lucide-react';
import { theme, inputStyle } from '../theme';

// ════════════════════════════════════════════════════════════════════
//  SmartFindPanel — shared UI for ranking + picking the best booking slot.
//
//  Backend contract (unchanged):  POST /api/smart-schedule/suggest
//  Expected response: { suggestions: [{ date, time, doctorId, doctorName,
//                        score, durationMin, timeBand, ... }], ... }
//
//  Used in two places with the same onPick callback contract so both the
//  calendar drawer (fresh booking) and the inline booking-modal hint
//  (adjust existing draft) can reuse this component.
//
//  Props:
//    API_BASE, patientsList, treatmentTypes  — from the parent app state
//    onPick(suggestion)                      — called with the chosen slot
//    initialTreatment, initialDuration,
//    initialDate, initialDoctorId            — pre-fill for inline use
//    compact                                 — denser layout for modal embed
// ════════════════════════════════════════════════════════════════════

const TIME_BAND_LABELS = {
  morning:   '🌅 Morning (≤ 12pm)',
  afternoon: '🌞 Afternoon (12–5pm)',
  evening:   '🌙 Evening (5pm+)',
};

const SmartFindPanel = ({
  API_BASE,
  patientsList = [],
  treatmentTypes = [],
  onPick,
  initialTreatment = '',
  initialDuration = '',
  initialDate = '',
  initialDoctorId = '',
  compact = false,
}) => {
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [form, setForm] = useState({
    patientId: '',
    treatmentTypeName: initialTreatment,
    durationMinutes: initialDuration ? String(initialDuration) : '',
    preferredTimeBand: '',
    preferredDoctorId: initialDoctorId,
    startDate: initialDate || todayISO,
  });
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Fetch doctors once so the "preferred doctor" dropdown has real names.
  useEffect(() => {
    axios.get(`${API_BASE}/doctors`)
      .then(r => setDoctors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDoctors([]));
  }, [API_BASE]);

  // When the caller updates initial* (e.g. user typed a treatment in the modal),
  // mirror that into the form so the suggestion payload stays accurate.
  useEffect(() => {
    setForm(f => ({
      ...f,
      treatmentTypeName: initialTreatment || f.treatmentTypeName,
      durationMinutes: initialDuration ? String(initialDuration) : f.durationMinutes,
      startDate: initialDate || f.startDate,
      preferredDoctorId: initialDoctorId || f.preferredDoctorId,
    }));
  }, [initialTreatment, initialDuration, initialDate, initialDoctorId]);

  const runSuggest = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const payload = { ...form };
      if (!payload.patientId) delete payload.patientId;
      if (!payload.preferredDoctorId) delete payload.preferredDoctorId;
      if (!payload.preferredTimeBand) delete payload.preferredTimeBand;
      if (!payload.treatmentTypeName) delete payload.treatmentTypeName;
      if (!payload.durationMinutes) delete payload.durationMinutes;
      else payload.durationMinutes = Number(payload.durationMinutes);

      const r = await axios.post(`${API_BASE}/smart-schedule/suggest`, payload);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Suggest failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (s) => {
    if (!onPick) return;
    onPick({
      date: s.date,
      time: s.time,
      endTime: s.endTime,
      duration: s.durationMin,
      doctorId: s.doctorId,
      doctorName: s.doctorName,
      treatment: form.treatmentTypeName || '',
    });
  };

  const pad = compact ? 10 : 14;

  return (
    <div style={{
      background: compact ? '#f7fbff' : 'white',
      padding: pad, borderRadius: 10,
      border: compact ? '1px dashed #bcd7e8' : 'none',
    }}>
      {!compact && (
        <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:10}}>
          <Sparkles size={18} color={theme.primary}/>
          <strong style={{color: theme.navy, fontSize:'1rem'}}>Smart Find Slot</strong>
        </div>
      )}

      <div style={{display:'grid', gridTemplateColumns: compact ? '1fr 1fr' : '1fr', gap: 8, marginBottom: 10}}>
        <div>
          <label style={{fontSize:'0.74rem', color:'#6c757d', fontWeight:600, display:'block', marginBottom:3}}>Treatment</label>
          <select value={form.treatmentTypeName}
            onChange={e => {
              const tt = treatmentTypes.find(t => t.name === e.target.value);
              setForm(f => ({
                ...f,
                treatmentTypeName: e.target.value,
                durationMinutes: tt?.defaultDuration ? String(tt.defaultDuration) : f.durationMinutes,
              }));
            }}
            style={{...inputStyle, padding:'6px 9px', fontSize:'0.84rem'}}>
            <option value="">(Any / custom)</option>
            {treatmentTypes.map(tt => (
              <option key={tt._id || tt.name} value={tt.name}>
                {tt.name}{tt.defaultDuration ? ` (${tt.defaultDuration}m)` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{fontSize:'0.74rem', color:'#6c757d', fontWeight:600, display:'block', marginBottom:3}}>Duration (min)</label>
          <input type="number" min="5" step="5" placeholder="30"
            value={form.durationMinutes}
            onChange={e => setForm(f => ({ ...f, durationMinutes: e.target.value }))}
            style={{...inputStyle, padding:'6px 9px', fontSize:'0.84rem'}}/>
        </div>

        <div>
          <label style={{fontSize:'0.74rem', color:'#6c757d', fontWeight:600, display:'block', marginBottom:3}}>Start searching from</label>
          <input type="date" value={form.startDate}
            onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
            style={{...inputStyle, padding:'6px 9px', fontSize:'0.84rem'}}/>
        </div>

        <div>
          <label style={{fontSize:'0.74rem', color:'#6c757d', fontWeight:600, display:'block', marginBottom:3}}>Time preference</label>
          <select value={form.preferredTimeBand}
            onChange={e => setForm(f => ({ ...f, preferredTimeBand: e.target.value }))}
            style={{...inputStyle, padding:'6px 9px', fontSize:'0.84rem'}}>
            <option value="">Any time</option>
            <option value="morning">🌅 Morning</option>
            <option value="afternoon">🌞 Afternoon</option>
            <option value="evening">🌙 Evening</option>
          </select>
        </div>

        <div style={{gridColumn: compact ? 'span 2' : 'span 1'}}>
          <label style={{fontSize:'0.74rem', color:'#6c757d', fontWeight:600, display:'block', marginBottom:3}}>Preferred doctor</label>
          <select value={form.preferredDoctorId}
            onChange={e => setForm(f => ({ ...f, preferredDoctorId: e.target.value }))}
            style={{...inputStyle, padding:'6px 9px', fontSize:'0.84rem'}}>
            <option value="">Any doctor</option>
            {doctors.map(d => (
              <option key={d._id} value={d._id}>{d.name}</option>
            ))}
          </select>
        </div>

        {!compact && patientsList.length > 0 && (
          <div>
            <label style={{fontSize:'0.74rem', color:'#6c757d', fontWeight:600, display:'block', marginBottom:3}}>Existing patient (optional)</label>
            <select value={form.patientId}
              onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}
              style={{...inputStyle, padding:'6px 9px', fontSize:'0.84rem'}}>
              <option value="">(None / new patient)</option>
              {patientsList.slice(0, 500).map(p => (
                <option key={p._id} value={p._id}>{p.name} — {p.phone}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <button onClick={runSuggest} disabled={loading}
        style={{
          width: '100%', padding: '9px 14px', borderRadius: 8, border: 'none',
          background: loading ? '#aaa' : theme.primary,
          color: 'white', fontWeight: 700, fontSize: '0.88rem',
          cursor: loading ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          boxShadow: loading ? 'none' : `0 2px 8px ${theme.primary}44`,
        }}>
        {loading ? <Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/> : <Sparkles size={14}/>}
        {loading ? 'Ranking slots…' : 'Find best slots'}
      </button>

      {error && (
        <div style={{marginTop:8, padding:'7px 10px', borderRadius:8, background:'#fee2e2', color:'#991b1b', fontSize:'0.8rem', fontWeight:600}}>
          ⚠️ {error}
        </div>
      )}

      {result && (
        <div style={{marginTop:10}}>
          <div style={{fontSize:'0.76rem', color:'#6c757d', marginBottom:6}}>
            {result.suggestions?.length
              ? <>Considered <b>{result.doctorsConsidered}</b> doctors · Duration <b>{result.durationMin}m</b> · Top {result.suggestions.length}</>
              : 'No slots found. Try widening the date range or changing the doctor filter.'}
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:6, maxHeight: compact ? 220 : 360, overflowY:'auto'}}>
            {(result.suggestions || []).map((s, i) => (
              <button key={`${s.date}-${s.time}-${s.doctorId}`} onClick={() => handlePick(s)}
                style={{
                  display:'grid', gridTemplateColumns:'auto 1fr auto', gap:10, alignItems:'center',
                  padding:'8px 11px', borderRadius:8, cursor:'pointer', textAlign:'left',
                  border: `1px solid ${i < 3 ? '#bbf7d0' : '#e5e7eb'}`,
                  background: i < 3 ? '#f0fdf4' : 'white',
                }}>
                <span style={{
                  fontWeight:800, fontSize:'0.76rem',
                  padding:'2px 8px', borderRadius:10,
                  background: i < 3 ? '#16a34a' : '#6b7280', color:'white',
                }}>
                  #{i+1}
                </span>
                <div style={{minWidth:0}}>
                  <div style={{fontWeight:700, fontSize:'0.88rem', color:'#111827'}}>
                    {s.date} · {s.time}{s.endTime ? ` – ${s.endTime}` : ''} ({s.durationMin}m)
                  </div>
                  <div style={{fontSize:'0.76rem', color:'#4b5563', marginTop:1}}>
                    👨‍⚕️ {s.doctorName || 'Any doctor'}
                    {s.timeBand && <span style={{marginLeft:8, color:'#6b7280'}}>{TIME_BAND_LABELS[s.timeBand] || s.timeBand}</span>}
                  </div>
                </div>
                <span style={{
                  fontSize:'0.7rem', color: i < 3 ? '#166534' : '#374151',
                  padding:'2px 7px', borderRadius:10, fontWeight:700,
                  background: i < 3 ? '#bbf7d0' : '#f3f4f6',
                }}>
                  Score {s.score}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* spinner keyframes */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
      `}</style>
    </div>
  );
};

export default SmartFindPanel;
